import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import EstimateModal from './EstimateModal';
import { estimateService } from '../services/estimateService';

const ViewEstimate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchEstimate = useCallback(async () => {
    try {
      setLoading(true);
      const res = await estimateService.getEstimate(id);
      setEstimate(res.data);
      setError('');
    } catch {
      setError('Estimate not found.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSaved = async (updated) => {
    setShowEditModal(false);
    await fetchEstimate();
    showSuccess('Estimate updated successfully!');
  };

  const handleDelete = async () => {
    try {
      await estimateService.deleteEstimate(id);
      navigate('/estimates', { replace: true });
    } catch {
      showSuccess('Failed to delete estimate.');
    }
  };

  const calcTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + parseFloat(item.value || 0), 0);
  };

  const formatValue = (value) => {
    return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-red-500">{error || 'Estimate not found.'}</div>
      </div>
    );
  }

  const total = calcTotal(estimate.items);
  const hasNegative = estimate.items?.some(i => parseFloat(i.value) < 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm flex-wrap">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <Link to="/estimates" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Estimates</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              {estimate.job && (
                <>
                  <Link to={`/jobs/${estimate.job.id}`} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">{estimate.job.title}</Link>
                  <span className="text-gray-400 dark:text-gray-600">/</span>
                </>
              )}
              <span className="text-gray-900 dark:text-white font-medium">{estimate.title}</span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <span className="text-sm text-gray-600 dark:text-gray-300">{user?.name}</span>
              <button
                onClick={logout}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm">
            {successMessage}
          </div>
        )}

        {/* Title + actions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{estimate.title}</h1>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {estimate.job && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Job</p>
                    <Link to={`/jobs/${estimate.job.id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                      {estimate.job.title}
                    </Link>
                    {estimate.job.customer_po && (
                      <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">PO: {estimate.job.customer_po}</p>
                    )}
                  </div>
                )}
                {estimate.job?.site && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Site</p>
                    <Link to={`/sites/${estimate.job.site_id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                      {estimate.job.site.name}
                    </Link>
                    {estimate.job.site.customer && (
                      <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">{estimate.job.site.customer.name}</p>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Created</p>
                  <p className="text-gray-900 dark:text-white">{formatDate(estimate.created_at)}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
              >
                Edit
              </button>
              {deleteConfirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-600 dark:text-red-400">Delete estimate?</span>
                  <button
                    onClick={handleDelete}
                    className="px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
                  >Yes</button>
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="px-3 py-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >No</button>
                </div>
              ) : (
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Line Items</h2>
          </div>

          {!estimate.items || estimate.items.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No line items.</p>
          ) : (
            <>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {estimate.items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{item.description}</td>
                      <td className={`px-6 py-4 text-sm font-medium text-right tabular-nums ${parseFloat(item.value) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                        {formatValue(item.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40">
                    <td className="px-6 py-4 text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Total</td>
                    <td className={`px-6 py-4 text-base font-bold text-right tabular-nums ${total < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                      {formatValue(total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              {hasNegative && (
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
                  Negative values represent discounts or credits.
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {showEditModal && (
        <EstimateModal
          jobId={estimate.job_id}
          estimate={estimate}
          onClose={() => setShowEditModal(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default ViewEstimate;
